import { i18n } from '@/i18n'
import { subscribeToCustomerRooms } from '@/util/customerSocketRooms'

/**
 * Tracks whether this page load has already completed its first socket
 * connection, plus which report ids were watched during this page load (as
 * opposed to restored from localStorage by vuex-persist). Neither can live
 * in vuex state: vuex-persist writes state to localStorage, and these
 * specifically need to reset on every page load so the first connection can
 * be told apart from a mid-session reconnect and stale persisted report ids
 * can be told apart from fresh ones. Exported so unit tests can reset it
 * between cases.
 */
export const connectionSession = {
  initialConnectComplete: false,
  watchedThisLoad: new Set()
}

/**
 * Emits socket events with our current customer context (if set) and user id
 * (also if set)
 *
 * @return undefined
 */
const registerState = function () {
  const currentCustomerContext = this.getters['userModule/customerContext']
  if (currentCustomerContext != null) {
    this._vm.$socket.client.emit('set-customer-context', currentCustomerContext.id)
  }

  const currentUser = this.getters['userModule/getUser']
  if (currentUser != null) {
    this._vm.$socket.client.emit('set-user', currentUser.id)
    subscribeToCustomerRooms(this._vm.$socket, currentUser)
  }
}

/**
 * Manages the state of the web-socket and performs initializations/tear-down
 * tasks
 */
export default {
  state: {
    isConnected: false,
    reconnectError: false,
    event: null,
    reconnect_attempt_count: 0,
    // Ids of reports whose generation we are currently awaiting. Used to
    // re-join the matching socket rooms after a reconnect and to decide
    // whether a report:update event deserves a banner (VF-3266).
    pendingReports: []
  },
  mutations: {
    /**
     * Fired when the application is able to establish a connection with the
     * socket server, calls the registerState method which updates the
     * socket server with our current user/customer state. Also reconciles
     * report-room membership: the socket server tracks rooms per connection,
     * so rooms joined before a disconnect are lost and must be re-joined
     * (VF-3266).
     *
     * @param {*} state state object defined above
     */
    SOCKET_CONNECT (state) {
      state.reconnectError = false
      state.reconnect_attempt_count = 0
      state.isConnected = true
      state.event = 'connected'

      registerState.apply(this)

      if (!connectionSession.initialConnectComplete) {
        // First connection of this page load. Report ids restored from
        // localStorage by vuex-persist are stale — their completion events
        // may have fired while no page was listening — so drop them. Ids
        // watched during this page load survive: their join emits were
        // buffered by socket.io-client and flush right after this connect.
        connectionSession.initialConnectComplete = true
        state.pendingReports = state.pendingReports.filter(
          (reportId) => connectionSession.watchedThisLoad.has(reportId)
        )
      } else {
        // Reconnection: re-join the room of every report we are still
        // awaiting so their report:update events reach us again. An event
        // that fired DURING the disconnect gap is not replayed by the
        // server; that notification is accepted as lost (the stale id is
        // dropped by the first-connect filter on the next page load).
        state.pendingReports.forEach((reportId) => {
          this._vm.$socket.client.emit('join', `report:${reportId}`)
        })
      }
    },

    /**
     * Updates connection internal state
     *
     * @param {*} state state object defined above
     * @throws Error
     */
    SOCKET_ERROR (state) {
      state.isConnected = false
      state.event = 'error'
    },

    /**
     * Updates internal connection state
     *
     * @param {*} state state object defined above
     */
    SOCKET_DISCONNECT (state) {
      state.isConnected = false
      state.reconnectError = false
      state.event = 'disconnect'
    },

    /**
     * Like SOCKET_CONNECT, also calls the registerState() method which
     * updates the socket server with our current user/customer state
     *
     * @param {*} state state object defined above
     */
    SOCKET_RECONNECT (state) {
      state.reconnectError = false
      state.event = 'reconnect'
      registerState.apply(this)
    },

    /**
     * Updates internal connection state
     *
     * @param {*} state state object defined above
     */
    SOCKET_RECONNECT_ERROR (state) {
      state.reconnectError = true
      state.event = 'reconnect_error'

      // It appears that throwing an error here prevents a reconnect from being
      // triggered
      // throw new Error(`socket.io error: ${state.event}`)
    },

    /**
     * Increments the reconnect attempt count
     *
     * @param {*} state state object defined above
     */
    SOCKET_RECONNECT_ATTEMPT (state) {
      state.reconnect_attempt_count = state.reconnect_attempt_count + 1
    },

    /**
     * Fired when the application is unable to establish a connection with
     * the socket server.
     *
     * @param {*} state state object defined above
     * @throws Error
     */
    SOCKET_CONNECT_ERROR (state) {
      state.isConnected = false
      state.event = 'connect_error'

      // It appears that throwing an error here prevents a reconnect from being
      // triggered
      // throw new Error(`socket.io error: ${state.event}`)
    },

    /**
     * Adds a report id to the list of reports awaiting generation (no-op if
     * it is already tracked)
     *
     * @param {*} state state object defined above
     * @param {number} reportId
     */
    addPendingReport (state, reportId) {
      if (!state.pendingReports.includes(reportId)) {
        state.pendingReports.push(reportId)
      }
    },

    /**
     * Removes a report id from the list of reports awaiting generation
     *
     * @param {*} state state object defined above
     * @param {number} reportId
     */
    removePendingReport (state, reportId) {
      state.pendingReports = state.pendingReports.filter((id) => id !== reportId)
    }
  },
  actions: {
    /**
     * Starts watching a report that is being generated: joins its socket
     * room and remembers the id so the room can be re-joined after a
     * reconnect (see SOCKET_CONNECT) and so socket_report:update knows which
     * reports to announce.
     *
     * @param {object} context object injected by vuex
     * @param {number} reportId
     */
    watchReport (context, reportId) {
      connectionSession.watchedThisLoad.add(reportId)
      this._vm.$socket.client.emit('join', `report:${reportId}`)
      context.commit('addPendingReport', reportId)
    },

    /**
     * Stops watching a report: leaves its socket room and forgets the id.
     *
     * @param {object} context object injected by vuex
     * @param {number} reportId
     */
    unwatchReport (context, reportId) {
      this._vm.$socket.client.emit('leave', `report:${reportId}`)
      context.commit('removePendingReport', reportId)
    },

    /**
     * Dispatched automatically by vue-socket.io-extended when the socket
     * server emits report:update (fired by the GenerateReport queue job when
     * a report finishes). If the user is currently on that report's page,
     * ReportView's own listener refreshes it and no banner is needed.
     * Otherwise show a persistent banner with a link to the report —
     * persistent because flash banners are cleared on every route change,
     * which previously made the notification easy to miss (VF-3266).
     *
     * NOTE the quoted action name: the library resolves an event to
     * "socket_" + camelCase(event-name), but its camelCase only treats
     * "_", ".", "-" and spaces as separators — a ":" passes through
     * verbatim. So the action for "report:update" must be named
     * "socket_report:update"; a conventional "socket_reportUpdate" would
     * never fire.
     *
     * @param {object} context object injected by vuex
     * @param {object} payload socket event payload ({ report_id: int })
     */
    'socket_report:update' (context, payload) {
      const reportId = payload && payload.report_id

      if (!reportId || !context.state.pendingReports.includes(reportId)) {
        return
      }

      context.dispatch('unwatchReport', reportId)

      const currentPath = window.location.pathname.replace(/\/+$/, '')
      if (currentPath !== `/report/${reportId}`) {
        context.dispatch(
          'globalMessagingModule/showSuccessBanner',
          i18n.t('report_ready', { report_id: reportId }),
          { root: true }
        )
      }
    }
  }
}
